import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import {
  TiffConversionError,
  cancelPendingTiffConversions,
  convertTiffToJpg,
  downloadBlob,
  formatSize,
  outputFilename,
  uniqueOutputFilename,
  validateTiffFile,
  type TiffErrors,
} from '../model/tiffProcessing'
import { useTiffConversionLifecycle } from '../model/useTiffConversionLifecycle'
import { LocalizedProcessingBadge, useT } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type TiffStatus = 'pending' | 'converting' | 'done' | 'error'

interface TiffCopy {
  eyebrow: string
  title: string
  description: string
  dropTitle: string
  dropDescription: string
  chooseFiles: string
  qualityLabel: string
  multiPageWarning: string
  privacyNote: string
  qualityHowTo: string
  errorsText: TiffErrors
  faq: Array<{ question: string; answer: string }>
}

interface TiffEntry {
  id: string
  file: File
  originalSize: number
  convertedBlob: Blob | null
  convertedUrl: string | null
  convertedSize: number | null
  width: number | null
  height: number | null
  status: TiffStatus
  error?: string
  warning?: string
}

interface TiffConverterPageProps {
  copy: TiffCopy
}

const UTILITY_KEY = 'tiff-to-jpg'
const ZIP_FILENAME = 'tiff-to-jpg.zip'

let nextId = 0

function uid(): string {
  return `tiff-${++nextId}-${Date.now()}`
}

function revokeEntry(entry: TiffEntry): void {
  if (entry.convertedUrl) {
    URL.revokeObjectURL(entry.convertedUrl)
  }
}

export function TiffConverterPage({ copy }: TiffConverterPageProps) {
  const t = useT()
  const [files, setFiles] = useState<TiffEntry[]>([])
  const [quality, setQuality] = useState(85)
  const [isConverting, setIsConverting] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const previousQualityRef = useRef(quality)
  const filesRef = useRef<TiffEntry[]>([])

  useEffect(() => {
    filesRef.current = files
  }, [files])

  useEffect(() => {
    if (previousQualityRef.current === quality) return
    previousQualityRef.current = quality

    setFiles((prev) =>
      prev.map((entry) => {
        if (entry.status === 'pending' || entry.status === 'error') return entry
        revokeEntry(entry)
        return {
          ...entry,
          convertedBlob: null,
          convertedUrl: null,
          convertedSize: null,
          width: null,
          height: null,
          status: 'pending' as const,
          error: undefined,
          warning: undefined,
        }
      }),
    )
  }, [quality])

  const cleanupTiffConversion = useCallback(() => {
    cancelPendingTiffConversions()
    filesRef.current.forEach(revokeEntry)
  }, [])
  const createCurrentOperationCheck = useTiffConversionLifecycle(cleanupTiffConversion)

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      if (isConverting) return

      const entries = Array.from(incoming).map<TiffEntry>((file) => {
        const validationError = validateTiffFile(file, copy.errorsText)
        if (validationError) {
          toolError(UTILITY_KEY, validationError.code)
          return {
            id: uid(),
            file,
            originalSize: file.size,
            convertedBlob: null,
            convertedUrl: null,
            convertedSize: null,
            width: null,
            height: null,
            status: 'error',
            error: validationError.message,
          }
        }

        return {
          id: uid(),
          file,
          originalSize: file.size,
          convertedBlob: null,
          convertedUrl: null,
          convertedSize: null,
          width: null,
          height: null,
          status: 'pending',
        }
      })

      setFiles((prev) => [...prev, ...entries])
    },
    [copy.errorsText, isConverting],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)

      if (isConverting) return

      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles, isConverting],
  )

  const removeFile = useCallback((id: string) => {
    if (isConverting) return

    setFiles((prev) => {
      const entry = prev.find((file) => file.id === id)
      if (entry) {
        revokeEntry(entry)
      }
      return prev.filter((file) => file.id !== id)
    })
  }, [isConverting])

  const clearAll = useCallback(() => {
    if (isConverting) return

    files.forEach(revokeEntry)
    setFiles([])
    nextId = 0
  }, [files, isConverting])

  const handleConvert = useCallback(async () => {
    toolInvoke(UTILITY_KEY)
    setIsConverting(true)
    const isCurrentOperation = createCurrentOperationCheck()

    const startTime = performance.now()
    const pending = files.filter((file) => file.status === 'pending')
    const updated = [...files]
    let runSuccessCount = 0

    for (const entry of pending) {
      const index = updated.findIndex((file) => file.id === entry.id)
      if (index === -1) continue

      updated[index] = { ...entry, status: 'converting', error: undefined }
      setFiles([...updated])

      try {
        const { blob, frames, width, height } = await convertTiffToJpg(
          entry.file,
          quality,
          copy.errorsText,
        )
        if (!isCurrentOperation()) return
        const convertedUrl = URL.createObjectURL(blob)
        updated[index] = {
          ...entry,
          convertedBlob: blob,
          convertedUrl,
          convertedSize: blob.size,
          width,
          height,
          status: 'done',
          warning: frames > 1 ? copy.multiPageWarning : undefined,
        }
        runSuccessCount += 1
        setFiles([...updated])
      } catch (error) {
        if (!isCurrentOperation()) return
        const code =
          error instanceof TiffConversionError ? error.code : 'conversion_failed'
        toolError(UTILITY_KEY, code)
        updated[index] = {
          ...entry,
          status: 'error',
          error: error instanceof Error ? error.message : copy.errorsText.generic,
        }
        setFiles([...updated])
      }
    }

    if (!isCurrentOperation()) return

    const durationMs = Math.round(performance.now() - startTime)
    if (runSuccessCount > 0) {
      toolSuccess(UTILITY_KEY, durationMs)
    } else {
      toolError(UTILITY_KEY, 'all_failed')
    }

    setIsConverting(false)
  }, [copy.errorsText, copy.multiPageWarning, createCurrentOperationCheck, files, quality])

  const handleDownloadOne = useCallback((entry: TiffEntry) => {
    if (!entry.convertedBlob) return
    downloadBlob(entry.convertedBlob, outputFilename(entry.file.name))
  }, [])

  const handleDownloadAll = useCallback(async () => {
    const done = files.filter((file) => file.status === 'done' && file.convertedBlob)
    if (done.length === 0) return

    const { default: JSZip } = await import('jszip')
    const zip = new JSZip()
    const usedNames = new Set<string>()

    for (const entry of done) {
      zip.file(uniqueOutputFilename(entry.file.name, usedNames), entry.convertedBlob!)
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    downloadBlob(zipBlob, ZIP_FILENAME)
  }, [files])

  const stats = useMemo(() => {
    const pending = files.filter((file) => file.status === 'pending').length
    const done = files.filter((file) => file.status === 'done').length
    const errors = files.filter((file) => file.status === 'error').length
    const totalOriginal = files.reduce((sum, file) => sum + file.originalSize, 0)
    const totalConverted = files
      .filter((file) => file.convertedSize !== null)
      .reduce((sum, file) => sum + (file.convertedSize ?? 0), 0)
    return { pending, done, errors, totalOriginal, totalConverted }
  }, [files])

  const canConvert = files.length > 0 && !isConverting && stats.pending > 0

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={['TIFF -> JPG', t.common.maxFileSizePerFile]}
        collapsed={files.length > 0}
      />

      <div
        className={cn(
          'grid grid-cols-1 gap-6',
          files.length > 0 && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start',
        )}
      >
        <div className="min-w-0 space-y-6">
          <Card
            className={cn(
              'mx-auto max-w-3xl transition-colors',
              files.length > 0 && 'max-w-none',
              dropActive && 'border-primary bg-primary/5',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                TIFF
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
              <p
                className={cn(
                  'mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground',
                  files.length > 0 && 'hidden sm:block',
                )}
              >
                {copy.dropDescription}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".tif,.tiff,image/tiff,image/x-tiff"
                multiple
                disabled={isConverting}
                className="hidden"
                onChange={handleInputChange}
              />
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  disabled={isConverting}
                  onClick={() => inputRef.current?.click()}
                >
                  {copy.chooseFiles}
                </Button>
                {files.length > 0 && (
                  <Button
                    variant="outline"
                    disabled={isConverting}
                    onClick={clearAll}
                  >
                    {t.converter.clearAll}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {files.length > 0 && (
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-normal">
                  {t.converter.filesTitle} ({files.length})
                </h2>
                <div className="flex gap-2">
                  {stats.done > 0 && (
                    <Button variant="outline" size="sm" onClick={handleDownloadAll}>
                      {t.converter.downloadAll} ({stats.done})
                    </Button>
                  )}
                  {canConvert && (
                    <Button size="sm" onClick={handleConvert}>
                      {t.common.convert} ({stats.pending})
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {files.map((entry) => {
                  const convertedDelta =
                    entry.originalSize > 0 && entry.convertedSize !== null
                      ? Math.round(
                          ((entry.convertedSize - entry.originalSize) / entry.originalSize) *
                            100,
                        )
                      : null

                  return (
                    <Card key={entry.id} className="overflow-hidden shadow-sm">
                      <CardContent className="p-0">
                        <div className="grid aspect-video place-items-center bg-muted">
                          {entry.convertedUrl ? (
                            <img
                              src={entry.convertedUrl}
                              alt={entry.file.name}
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <div className="rounded-md border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground">
                              TIFF
                            </div>
                          )}
                        </div>

                        <div className="space-y-4 p-4">
                          <div className="min-w-0">
                            <div
                              className="truncate text-sm font-medium"
                              title={entry.file.name}
                            >
                              {entry.file.name}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                                TIFF
                              </span>
                              <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                {formatSize(entry.originalSize)}
                              </span>
                              {entry.width !== null && entry.height !== null && (
                                <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                  {entry.width}x{entry.height}
                                </span>
                              )}
                              {entry.convertedSize !== null && (
                                <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                                  {formatSize(entry.convertedSize)}
                                  {convertedDelta !== null && (
                                    <span
                                      className={cn(
                                        'ml-1 font-medium',
                                        convertedDelta <= 0
                                          ? 'text-primary'
                                          : 'text-muted-foreground',
                                      )}
                                    >
                                      {convertedDelta > 0 ? '+' : ''}
                                      {convertedDelta}%
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          {entry.status === 'converting' && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                              {t.common.converting}
                            </div>
                          )}

                          {entry.status === 'done' && entry.convertedBlob && (
                            <div className="space-y-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="text-sm font-medium text-primary">
                                  {t.common.done}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownloadOne(entry)}
                                >
                                  {t.common.download}
                                </Button>
                              </div>
                              {entry.warning && (
                                <p className="text-xs leading-5 text-muted-foreground">
                                  {entry.warning}
                                </p>
                              )}
                            </div>
                          )}

                          {entry.status === 'error' && (
                            <div className="text-sm text-destructive">
                              {entry.error ?? t.common.errorConverting}
                            </div>
                          )}

                          {entry.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isConverting}
                              className="text-destructive hover:text-destructive"
                              onClick={() => removeFile(entry.id)}
                            >
                              {t.common.remove}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </section>
          )}
        </div>

        {files.length > 0 && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">
                    {t.converter.settings}
                  </h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium">{copy.qualityLabel}</span>
                    <span className="text-sm tabular-nums text-muted-foreground">
                      {quality}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={40}
                    max={100}
                    step={1}
                    value={quality}
                    disabled={isConverting}
                    onChange={(event) => setQuality(Number(event.target.value))}
                    className="w-full accent-primary"
                  />
                </div>

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleConvert}
                  disabled={!canConvert}
                >
                  {isConverting
                    ? t.common.converting
                    : stats.pending > 0
                      ? `${t.common.convert} (${stats.pending})`
                      : t.converter.noFilesQueued}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{t.converter.summary}</h2>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.pending}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.pending}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.done}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.done}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="font-semibold tabular-nums">{stats.errors}</div>
                    <div className="text-xs text-muted-foreground">{t.converter.errors}</div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{t.converter.originalSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(stats.totalOriginal)}
                    </strong>
                  </div>
                  {stats.done > 0 && (
                    <div className="flex justify-between gap-3">
                      <span>{t.converter.convertedSize}</span>
                      <strong className="font-medium text-foreground">
                        {formatSize(stats.totalConverted)}
                      </strong>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {copy.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}
